import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery } from '../../services/baseQuery'

export interface MfaContact {
  masked: string
  verified: boolean | null
}

export interface MfaSelfServiceState {
  enabledMethods: string[]
  preferredMethod: string | null
  contacts: {
    phone: MfaContact | null
    email: MfaContact | null
  }
  legacyMfaOptions?: true
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isContact = (value: unknown): value is MfaContact | null =>
  value === null ||
  (isRecord(value) &&
    typeof value.masked === 'string' &&
    value.masked.length > 0 &&
    (typeof value.verified === 'boolean' || value.verified === null))

const isMfaSelfServiceState = (value: unknown): value is MfaSelfServiceState =>
  isRecord(value) &&
  Array.isArray(value.enabledMethods) &&
  value.enabledMethods.every(
    (method: unknown) => typeof method === 'string' && method.length > 0
  ) &&
  ((typeof value.preferredMethod === 'string' && value.preferredMethod.trim().length > 0) ||
    value.preferredMethod === null) &&
  isRecord(value.contacts) &&
  isContact(value.contacts.phone) &&
  isContact(value.contacts.email) &&
  (value.legacyMfaOptions === undefined || value.legacyMfaOptions === true)

export const mfaErrorCode = (error: unknown): string | undefined =>
  isRecord(error) && isRecord(error.data) && typeof error.data.code === 'string'
    ? error.data.code
    : undefined

// Client-side hint only, used to avoid a pointless BFF call; the BFF stays authoritative.
export const hasFederatedIdentity = (idToken: string | null): boolean => {
  const payload = idToken?.split('.')[1]
  if (!payload) return false
  try {
    const claims: unknown = JSON.parse(
      decodeURIComponent(
        Array.from(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
          .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      )
    )
    if (!isRecord(claims)) return false
    const identities =
      typeof claims.identities === 'string' ? JSON.parse(claims.identities) : claims.identities
    return Array.isArray(identities) && identities.length > 0
  } catch {
    return false
  }
}

export const mfaSelfServiceApi = createApi({
  reducerPath: 'mfaSelfServiceApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/bff/mfa-self-service' }),
  endpoints: (build) => ({
    getMfaSelfServiceState: build.query<MfaSelfServiceState, string>({
      query: () => ({ url: '/current', method: 'get' }),
      transformResponse: (response: unknown) => {
        if (!isMfaSelfServiceState(response)) throw new Error('Invalid MFA state response')
        return response
      },
      keepUnusedDataFor: 0,
    }),
  }),
})

export const { useGetMfaSelfServiceStateQuery } = mfaSelfServiceApi
